import { EmailConsent } from '@components/consents/EmailConsent';
import { Body, Heading2 } from '@components/utils/text';
import { FORM } from '@constants';
import styled from '@emotion/styled';
import {
    FormContext,
    FormShape,
    OutboundLinkContext,
    TrackEvent,
} from '@enums';
import {
    ComponentConsentsEmail,
    Enum_Forms_Type,
} from '@graphql/generated/graphql';
import { UseFormSubmitMutate } from '@hooks';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { Button, FormControl, Input, VStack } from '@lib/uikit';
import { getDefaultEmailConsent } from '@utils/consents';
import { Field, FieldProps, Form as FormikForm, Formik } from 'formik';
import { useTranslation } from 'next-i18next';
import { FC, useState } from 'react';
import * as yup from 'yup';

interface Props {
    formName: string;
    formUID: string;
    formType: Enum_Forms_Type;
    formContext?: FormContext;
    formShape: FormShape;
    companyName: string;
    title?: string;
    description?: string;
    isNameRequired: boolean;
    emailConsent?: ComponentConsentsEmail;
    ctaText?: string;
    isSubmitting: boolean;
    onSubmit: UseFormSubmitMutate;
}

interface FormData {
    email: string;
    firstname?: string;
    lastname?: string;
    optIn?: boolean;
}

const Form = styled(FormikForm)`
    width: 100%;
`;

export const NewsletterForm: FC<Props> = ({
    formName,
    formUID,
    formContext,
    formShape,
    formType,
    title,
    description,
    isNameRequired,
    emailConsent,
    ctaText,
    companyName,
    isSubmitting,
    onSubmit,
}) => {
    const { track } = useAnalytics();
    const { t } = useTranslation(FORM);
    const [formHasBeenFocused, setFormHasBeenFocused] =
        useState<boolean>(false);

    const defaultInitialValues: FormData = {
        email: '',
        optIn: getDefaultEmailConsent(emailConsent),
    };

    const requiredNameInitialValues: FormData = {
        ...defaultInitialValues,
        firstname: '',
        lastname: '',
    };

    const createValidationSchema = (isRequired?: boolean) => {
        const optInSchema = yup.boolean();

        if (isRequired) {
            optInSchema.oneOf([true], t('errors.required'));
        }

        return yup.object({
            email: yup
                .string()
                .email(t('errors.email.format'))
                .required(t('errors.required')),
            optIn: optInSchema,
        });
    };

    const defaultValidationSchema = createValidationSchema(
        emailConsent?.IsRequired
    );

    const requiredNameValidationSchema = isNameRequired
        ? defaultValidationSchema.concat(
              yup.object({
                  firstname: yup.string().required(t('errors.required')),
                  lastname: yup.string().required(t('errors.required')),
              })
          )
        : defaultValidationSchema;

    const handleFocus = () => {
        if (!formHasBeenFocused) {
            track(TrackEvent.FormStarted, {
                name: formName,
                form_uid: formUID,
                type: formType,
                context: formContext,
                shape: formShape,
            });
        }

        setFormHasBeenFocused(true);
    };

    return (
        <VStack>
            <VStack spacing='xs' align='center' textAlign='center'>
                {title && <Heading2>{title}</Heading2>}
                {description && <Body>{description}</Body>}
            </VStack>
            <Formik
                validateOnBlur={false}
                validateOnMount={false}
                validateOnChange={false}
                initialValues={
                    isNameRequired
                        ? requiredNameInitialValues
                        : defaultInitialValues
                }
                validationSchema={
                    isNameRequired
                        ? requiredNameValidationSchema
                        : defaultValidationSchema
                }
                onSubmit={(data) =>
                    onSubmit({
                        customerInputType: 'CUSTOM',
                        data: { ...data },
                    })
                }
            >
                {({ isValid }) => (
                    <Form onFocus={handleFocus}>
                        <VStack spacing='xs'>
                            <Field type='email' name='email'>
                                {({ form, field }: FieldProps) => (
                                    <FormControl
                                        name='email'
                                        errorMessage={
                                            form.errors.email as string
                                        }
                                    >
                                        <Input
                                            {...field}
                                            type='email'
                                            placeholder={
                                                t('labels.email') + '*'
                                            }
                                        />
                                    </FormControl>
                                )}
                            </Field>
                            {isNameRequired && (
                                <>
                                    <Field type='text' name='firstname'>
                                        {({ form, field }: FieldProps) => (
                                            <FormControl
                                                name='firstname'
                                                errorMessage={
                                                    form.errors
                                                        .firstname as string
                                                }
                                            >
                                                <Input
                                                    {...field}
                                                    type='text'
                                                    placeholder={
                                                        t('labels.firstName') +
                                                        '*'
                                                    }
                                                />
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field type='text' name='lastname'>
                                        {({ form, field }: FieldProps) => (
                                            <FormControl
                                                name='lastname'
                                                errorMessage={
                                                    form.errors
                                                        .lastname as string
                                                }
                                            >
                                                <Input
                                                    {...field}
                                                    type='text'
                                                    placeholder={
                                                        t('labels.lastName') +
                                                        '*'
                                                    }
                                                />
                                            </FormControl>
                                        )}
                                    </Field>
                                </>
                            )}
                            {emailConsent && !emailConsent.ImplicitConsent && (
                                <EmailConsent
                                    companyName={companyName}
                                    element={emailConsent}
                                    outboundLinkContext={
                                        OutboundLinkContext.NewsletterModal
                                    }
                                />
                            )}
                            <Button
                                disabled={!isValid}
                                isFullWidth
                                colorScheme='black'
                                type='submit'
                                isLoading={isSubmitting}
                            >
                                {ctaText || t('submit')}
                            </Button>
                            {emailConsent?.ImplicitConsent && (
                                <EmailConsent
                                    companyName={companyName}
                                    element={emailConsent}
                                    outboundLinkContext={
                                        OutboundLinkContext.NewsletterModal
                                    }
                                />
                            )}
                        </VStack>
                    </Form>
                )}
            </Formik>
        </VStack>
    );
};
